import type { CatalogItem } from '@/services/catalog'

/** Operational + lifecycle. inactive/discontinued = archived (no hard-delete). */
export type ProductStatus = 'ok' | 'pending' | 'inactive' | 'discontinued'

export const PRODUCT_STATUS_OPTIONS: Array<{
  value: ProductStatus
  label: string
}> = [
  { value: 'ok', label: 'OK' },
  { value: 'pending', label: 'Đang cập nhật' },
  { value: 'inactive', label: 'Ngưng dùng' },
  { value: 'discontinued', label: 'Ngừng SX' },
]

export const ARCHIVED_STATUSES: ProductStatus[] = ['inactive', 'discontinued']

export type ProductCustomer = {
  id: string
  code: string
  name: string
  status: string
}

/** Identity + resolved attribute chips for UI. */
export type Product = {
  id: string
  code: string
  name: string
  customerId?: string | null
  customer?: ProductCustomer | null
  w: number
  d: number
  h: number
  status: ProductStatus
  note: string
  imageUrl?: string
  colorIds: string[]
  woodIds: string[]
  veneerIds: string[]
  colors: CatalogItem[]
  woods: CatalogItem[]
  veneers: CatalogItem[]
}

export type ProductInput = {
  code: string
  name: string
  customerId?: string | null
  w: number
  d: number
  h: number
  status: ProductStatus
  note: string
  imageUrl?: string
  colorIds: string[]
  woodIds: string[]
  veneerIds: string[]
}

export type ProductUpdateInput = Omit<Partial<ProductInput>, 'code'>

export type ProductListParams = {
  /** true: server bỏ ảnh khỏi danh sách (nhẹ hơn nhiều); dùng cho ô chọn/tìm mã hàng. */
  lite?: boolean
  search?: string
  status?: ProductStatus | 'all' | 'active'
  customerId?: string
  colorIds?: string[]
  woodIds?: string[]
  veneerIds?: string[]
  sortBy?: 'code' | 'name' | 'w' | 'd' | 'h' | 'status'
  sortDir?: 'asc' | 'desc'
  page?: number
  pageSize?: number
}

export type PagedResult<T> = {
  items: T[]
  total: number
  page: number
  pageSize: number
}

export type ItemCodeHistory = {
  id: string
  productId: string
  oldCode: string
  newCode: string
  changedAt: string
  changedBy: string
  reason: string
}

export type ChangeItemCodeInput = {
  newCode: string
  reason: string
  changedBy?: string
}

export function formatDimension(product: Pick<Product, 'w' | 'd' | 'h'>): string {
  return `${product.w}×${product.d}×${product.h}`
}

export function formatDimensionInch(mm: number | null | undefined): number {
  if (mm == null || !Number.isFinite(mm) || mm <= 0) return 0
  const inch = mm / 25.4
  // If close to integer (within 0.05), round to integer
  if (Math.abs(inch - Math.round(inch)) < 0.05) {
    return Math.round(inch)
  }
  // If close to 1 decimal place (within 0.2/10 = 0.02)
  if (Math.abs(inch * 10 - Math.round(inch * 10)) < 0.2) {
    return Math.round(inch * 10) / 10
  }
  return Number(inch.toFixed(2))
}

export function roundMmUp(val: number | null | undefined): number {
  if (val == null || !Number.isFinite(val) || val <= 0) return 0
  return Math.ceil(val)
}

export function inchToMm(inch: number | null | undefined): number {
  if (inch == null || !Number.isFinite(inch) || inch <= 0) return 0
  return Math.ceil(inch * 25.4)
}

export function statusLabel(status: ProductStatus): string {
  return PRODUCT_STATUS_OPTIONS.find((item) => item.value === status)?.label ?? status
}

export function formatAttributeNames(items: CatalogItem[]): string {
  if (!items.length) return '—'
  return items.map((item) => item.name).join(', ')
}

export function isArchivedStatus(status: ProductStatus): boolean {
  return ARCHIVED_STATUSES.includes(status)
}
